// TelcoTrack — RSU / RAY-TR3 occupancy (OFSM modules, not MUK cassette slots).
// Weld / patch / splitter path stay first-class; pigtail is implicit on a weld.

import type { RayOfsmConnectorType, RayOfsmSap } from './rozvadec-ofsm.constants';

export const RSU_OCCUPANCY_VERSION = 1 as const;
export type RsuOccupancyVersion = typeof RSU_OCCUPANCY_VERSION;

export const RSU_TERMINATION_STATUSES = ['connected', 'reserve', 'free'] as const;
export type RsuTerminationStatus = (typeof RSU_TERMINATION_STATUSES)[number];

export type RsuOfsmModule = {
  id: string;
  materialSap: RayOfsmSap;
  /** 1 = topmost U of the 42U RAY-TR3. */
  rackStartU: number;
  heightU: 1 | 2 | 3;
  /** D5 ODF order, top → bottom. Recalculated on place/move. */
  odfOrder: number;
  connectorType: RayOfsmConnectorType | null;
};

export type RsuTermination = {
  id: string;
  moduleId: string;
  connectorPosition: number;
  trayNumber: number;
  trayWeldPosition: number;
  cableId: string | null;
  physicalFiberNumber: number | null;
  logicalFiberNumber: number | null;
  connectorType: RayOfsmConnectorType | string;
  destinationObjectId: string | null;
  destinationLabel: string | null;
  status: RsuTerminationStatus;
};

/** Cable fiber ↔ implicit pigtail / OFSM rear weld seat. */
export type RsuWeldConnection = {
  id: string;
  moduleId: string;
  terminationId: string;
  cableId: string;
  physicalFiberNumber: number;
  fiberRange?: { from: number; to: number };
  physicalWeldOperationCount?: number;
  splicedFiberCount?: number;
};

export type RsuPatchPortRef =
  | { kind: 'ofsm-connector'; moduleId: string; connectorPosition: number }
  | { kind: 'splitter-in'; moduleId: string; splitterIndex: number }
  | { kind: 'splitter-out'; moduleId: string; splitterIndex: number; output: number }
  /** Rail chip ↔ splitter IN/OUT (MUK overlay). Not a weld. */
  | { kind: 'cable-fiber'; cableId: string; physicalFiber: number };

/** Front patchcord: OFSM or rail fiber ↔ splitter IN/OUT, or OFSM ↔ OFSM. */
export type RsuPatchConnection = {
  id: string;
  source: RsuPatchPortRef;
  target: RsuPatchPortRef;
};

export type RsuSplitterPath = {
  id: string;
  moduleId: string;
  splitterIndex: number;
  inputPatchId: string | null;
  outputPatches: Array<{ output: number; patchId: string | null }>;
};

export type RsuOccupancy = {
  version: RsuOccupancyVersion;
  modules: RsuOfsmModule[];
  terminations: RsuTermination[];
  weldConnections: RsuWeldConnection[];
  patchConnections: RsuPatchConnection[];
  splitterPaths: RsuSplitterPath[];
};

export const RSU_OCCUPANCY_ISSUE_CODES = [
  'VERSION_UNSUPPORTED',
  'MODULE_SAP_INVALID',
  'MODULE_SPAN_INVALID',
  'MODULE_OVERLAP',
  'MODULE_MATERIAL_QTY',
  'TERMINATION_MODULE_MISSING',
  'TERMINATION_POSITION_INVALID',
  'TERMINATION_POSITION_OCCUPIED',
  'WELD_TERMINATION_MISSING',
  'PATCH_PORT_INVALID',
  'SPLITTER_MODULE_INVALID',
] as const;
export type RsuOccupancyIssueCode = (typeof RSU_OCCUPANCY_ISSUE_CODES)[number];

export type RsuOccupancyIssue = {
  code: RsuOccupancyIssueCode;
  path: string;
  message: string;
};
